import React, { useEffect, useState } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Divider,
  Button,
  Switch,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import api from '../../../api/axios';
import { RootState } from '../../../store';
import Lottie from 'lottie-react';
import loadingAnimation from '../../../assets/lottie/loading.json';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import { Settings, NotificationsNone, Sort, Language, Computer, MailOutline } from '@mui/icons-material';
import BaseModal from '../../utils/BaseModal';
import { toast } from 'react-toastify';
import {clearUnreadNotifications} from "../../../store/slices/authSlice";

type NotificationType = 'SUCCESS' | 'INFO' | 'WARNING' | 'ERROR';

type UserNotificationResponseDTO = {
  id: string;
  type: NotificationType;
  message: string;
  read: boolean;
  redirectTo?: string | null;
  creationDate: string;
  updatedDate?: string | null;
};

type NotificationChannel = 'WEB' | 'BROWSER' | 'EMAIL';

interface UserNotificationSettingsDTO {
  key: string;
  web: boolean;
  browser: boolean;
  email: boolean;
  mandatoryEmail: boolean;
}

const Notification = () => {
  const { t } = useTranslation();
  const userId = useSelector((state: RootState) => state.auth.userId);

  const [notifications, setNotifications] = useState<UserNotificationResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('creationDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [settings, setSettings] = useState<UserNotificationSettingsDTO[]>([]);
  const dispatch = useDispatch();

  const fetchNotifications = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await api.get('/user/notifications', {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortDirection}`,
        },
      });
      setNotifications(response.data.content);
      setTotalCount(response.data.totalElements);
      dispatch(clearUnreadNotifications());
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/profile/notification-settings');
      setSettings(response.data);
    } catch (e) {
      console.error("Error fetching settings:", e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchSettings();
  }, [userId, page, rowsPerPage, sortBy, sortDirection]);

  const handleRequestSort = (property: string) => {
    const isAsc = sortBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const handleRowClick = (n: UserNotificationResponseDTO) => {
    if (n.redirectTo && n.redirectTo.trim() !== '') {
      window.open(n.redirectTo, '_blank');
    }
  };

  const toggleSetting = async (key: string, channel: NotificationChannel, currentState: boolean) => {
    // Handling for Newsletter subscription
    if (key === 'NEWSLETTER') {
      if (channel !== 'EMAIL') return;
      if (!currentState) {
        try {
          await api.post('profile/newsletter/subscribe');
          setSettings(prev => prev.map(s => s.key === 'NEWSLETTER' ? { ...s, email: true } : s));
          toast.success("Subscribed to newsletter");
        } catch (e) {
          toast.error("Failed to subscribe");
        }
      } else {
        window.open('/newsletter/unsubscribe', '_blank');
      }
      return;
    }

    try {
      const payload = {
        key: key,
        channel: channel,
        enabled: !currentState
      };

      await api.post('/profile/notification-settings/update', payload);

      setSettings(prev => prev.map(s => {
        if (s.key === key) {
          return { ...s, [channel.toLowerCase()]: !currentState };
        }
        return s;
      }));
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const getNotificationStyle = (type: NotificationType, read: boolean) => {
    if (read) return 'opacity-60';
    switch (type) {
      case 'SUCCESS': return 'border-l-4 border-emerald-500';
      case 'ERROR': return 'border-l-4 border-red-500';
      case 'WARNING': return 'border-l-4 border-yellow-500';
      case 'INFO': return 'border-l-4 border-blue-500';
      default: return '';
    }
  };

  const getLabel = (key: string) => {
    return key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
      <div className="flex flex-col md:p-4 p-2 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 min-h-[88vh]">
        <div className="flex flex-row justify-between items-center gap-4 mb-2 shrink-0">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter">
            {t('profile.notifications.title', 'Notifications')}
          </h2>

          <Button
              variant="contained"
              className="h-[36px]"
              color="primary"
              sx={{ borderRadius: 0, boxShadow: 'none', border: '1px solid rgba(255,255,255,0.05)' }}
              startIcon={<Settings sx={{ fontSize: 18 }} />}
              onClick={() => setSettingsOpen(true)}
          >
            Settings
          </Button>
        </div>

        <Divider className="!border-gray-100 dark:!border-white/5" />

        {loading && notifications.length === 0 ? (
            <div className="flex-1 flex justify-center items-center">
              <Lottie animationData={loadingAnimation} loop className="w-64" />
            </div>
        ) : notifications.length > 0 ? (
            <TableContainer component={Paper} className="bg-transparent shadow-none border-none mt-4 overflow-hidden" sx={{ borderRadius: 0 }}>
              <Table size="small" className="border-separate border-spacing-y-2">
                <TableHead>
                  <TableRow className="bg-gray-50/50 dark:bg-white/[0.02]">
                    <TableCell sx={{ border: 'none' }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest w-16">
                      <TableSortLabel
                          active={sortBy === 'read'}
                          direction={sortBy === 'read' ? sortDirection : 'asc'}
                          onClick={() => handleRequestSort('read')}
                          IconComponent={Sort}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ border: 'none', display: { xs: 'none', sm: 'table-cell' } }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest w-40">
                      <TableSortLabel
                          active={sortBy === 'creationDate'}
                          direction={sortBy === 'creationDate' ? sortDirection : 'asc'}
                          onClick={() => handleRequestSort('creationDate')}
                          IconComponent={Sort}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ border: 'none' }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Message
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.map((n) => (
                      <TableRow
                          key={n.id}
                          onClick={() => handleRowClick(n)}
                          className={`group bg-gray-50/30 dark:bg-neutral-900/20 hover:bg-gray-100/50 dark:hover:bg-white/[0.03] transition-all cursor-pointer ${getNotificationStyle(n.type, n.read)}`}
                      >
                        <TableCell sx={{ border: 'none' }}>
                          {!n.read && (
                              <div className="w-2 h-2 rounded-full bg-dark-primary animate-pulse" />
                          )}
                        </TableCell>
                        <TableCell sx={{ border: 'none', display: { xs: 'none', sm: 'table-cell' } }}>
                    <span className="text-[10px] text-gray-400 font-medium">
                      {new Date(n.creationDate).toLocaleString('de-DE')}
                    </span>
                        </TableCell>
                        <TableCell sx={{ border: 'none' }}>
                          <div className="flex flex-col">
                      <span className={`text-xs sm:text-sm ${n.read ? 'text-gray-500' : 'text-gray-800 dark:text-gray-200 font-bold'}`}>
                        {n.message}
                      </span>
                          </div>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        ) : (
            <div className="flex-1 flex flex-col items-center justify-center opacity-40">
              <NotificationsNone sx={{ fontSize: 80 }} className="text-gray-300 mb-2" />
              <p className="text-sm font-bold text-gray-400 uppercase tracking-widest italic">
                No notifications found
              </p>
            </div>
        )}

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-100 dark:border-white/5 mt-auto shrink-0">
          <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{
                border: 'none',
                color: 'gray',
                '& .MuiTablePagination-actions .MuiButtonBase-root': {
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  color: '#3b82f6',
                  borderRadius: 0,
                  margin: '0 4px'
                }
              }}
          />
          <div className="text-[10px] text-gray-400 font-mono">{totalCount} total notifications</div>
        </div>

        <BaseModal
            open={settingsOpen}
            title="Notification Settings"
            onAbort={() => setSettingsOpen(false)}
            maxWidth="md"
        >
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-12 gap-4 px-4 pb-2 border-b border-neutral-100 dark:border-neutral-800">
              <div className="col-span-6">
                <p className="text-[10px] font-black text-neutral-500 uppercase tracking-widest">Event Type</p>
              </div>
              <div className="col-span-2 flex flex-col items-center gap-1">
                <Language sx={{ fontSize: 14 }} className="text-neutral-400" />
                <p className="text-[9px] font-bold text-neutral-500 uppercase">Website</p>
              </div>
              <div className="col-span-2 flex flex-col items-center gap-1">
                <Computer sx={{ fontSize: 14 }} className="text-neutral-400" />
                <p className="text-[9px] font-bold text-neutral-500 uppercase">Browser</p>
              </div>
              <div className="col-span-2 flex flex-col items-center gap-1">
                <MailOutline sx={{ fontSize: 14 }} className="text-neutral-400" />
                <p className="text-[9px] font-bold text-neutral-500 uppercase">Email</p>
              </div>
            </div>

            <div className="bg-neutral-50/50 dark:bg-black/20 rounded-xl border border-neutral-100 dark:border-neutral-800 divide-y divide-neutral-100 dark:divide-neutral-800/50">
              {settings.map((setting) => (
                  <div key={setting.key} className="grid grid-cols-12 items-center p-4">
                    <div className="col-span-6 flex flex-col">
                      <span className="text-sm font-bold dark:text-white">{getLabel(setting.key)}</span>
                      <span className="text-[10px] text-neutral-500">
                        {setting.key === 'NEWSLETTER' ? 'Weekly updates and promotions' : 'Alert me via selected channels'}
                      </span>
                    </div>

                    {/* Channel Website */}
                    <div className="col-span-2 flex justify-center">
                      {setting.key !== 'NEWSLETTER' && (
                          <Switch
                              checked={setting.web}
                              onChange={() => toggleSetting(setting.key, 'WEB', setting.web)}
                              size="small"
                              color="primary"
                          />
                      )}
                    </div>

                    {/* Channel Browser */}
                    <div className="col-span-2 flex justify-center">
                      {setting.key !== 'NEWSLETTER' && (
                          <Switch
                              checked={setting.browser}
                              onChange={() => toggleSetting(setting.key, 'BROWSER', setting.browser)}
                              size="small"
                              color="primary"
                          />
                      )}
                    </div>

                    {/* Channel Email */}
                    <div className="col-span-2 flex justify-center">
                      <Tooltip title={setting.mandatoryEmail ? "Required for security" : ""}>
                    <span>
                      <Switch
                          checked={setting.email}
                          onChange={() => toggleSetting(setting.key, 'EMAIL', setting.email)}
                          disabled={setting.mandatoryEmail}
                          size="small"
                          color={setting.mandatoryEmail ? "secondary" : "primary"}
                      />
                    </span>
                      </Tooltip>
                    </div>
                  </div>
              ))}
            </div>

            <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-lg">
              <p className="text-[11px] text-blue-400 italic">
                Note: "Item Purchased" and "Buy Order" emails are mandatory and cannot be disabled.
              </p>
            </div>
          </div>
        </BaseModal>
      </div>
  );
};

export default Notification;